import React, { useEffect, useRef } from "react";
import * as Mui from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { designColors } from "services/constants/designTokens";

// Wide Pay Inputs tables: the table scrolls sideways inside its own card
// instead of the whole page, while the column header stays stuck under the
// toolbar. Same technique as Team Workspace > Monthly roster (PlanShift/
// Monthly.js) and Reports > Working Hours:
//   - the header is a separate sticky strip that follows the body's
//     horizontal scroll through a scroll-driven CSS animation (same frame
//     as the body, so header and body columns cannot drift apart);
//   - the body's own scrollbar is hidden, because on a long list it only
//     shows at the very end; a sticky "proxy" bar pinned to the bottom of
//     the screen (just above the pagination footer) replaces it.
// Header and body are two tables, so they share one fixed column width
// list (`colWidths`) to stay aligned. Leading columns can be pinned to the
// left (like Monthly's Employee Name column) with pinnedColumns/pinnedCellSx.

export const SUPPORTS_SCROLL_TIMELINE =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("animation-timeline: scroll()");

const headShift = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(var(--pi-range, 0px) * -1)); }
`;

const TIMELINE = "--pix";

// The wide boxes take the card's width but never widen it: width 0 +
// min-width 100% means they add nothing to the page's intrinsic width.
// Without this the app's <main> (a flex item sized by its content) grows to
// the table's full width, so the table never scrolls inside its card, the
// page scrolls sideways and the footer's right half ends up off-screen.
const fillWithoutWideningSx = { width: 0, minWidth: "100%" };

const hiddenScrollbarSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

// Wraps header strip + body so the body's scroll timeline reaches the header.
export const timelineScopeSx = SUPPORTS_SCROLL_TIMELINE ? { timelineScope: TIMELINE } : {};

// Table sx shared by header and body: fixed layout at the summed width.
export const fixedTableSx = (colWidths) => ({
  width: `${colWidths.reduce((total, width) => total + width, 0)}px`,
  minWidth: "100%",
  tableLayout: "fixed",
});

export const ColGroup = ({ colWidths }) => (
  <colgroup>
    {colWidths.map((width, index) => (
      <col key={index} style={{ width }} />
    ))}
  </colgroup>
);

// deps: anything that changes the table's width or rows (columns, page…).
export const useStickyHorizontalScroll = (deps) => {
  const headRef = useRef(null);
  const bodyRef = useRef(null);
  const proxyRef = useRef(null);
  const proxyInnerRef = useRef(null);

  useEffect(() => {
    const body = bodyRef.current;
    const head = headRef.current;
    const proxy = proxyRef.current;
    const proxyInner = proxyInnerRef.current;
    if (!body || !head || !proxy || !proxyInner) return undefined;
    const publish = () => {
      head.style.setProperty("--pi-range", `${Math.max(0, body.scrollWidth - body.clientWidth)}px`);
      proxyInner.style.width = `${body.scrollWidth}px`;
      proxy.style.display = body.scrollWidth > body.clientWidth + 1 ? "block" : "none";
    };
    publish();
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(publish);
      observer.observe(body);
      if (body.firstElementChild) observer.observe(body.firstElementChild);
    }
    window.addEventListener("resize", publish);
    // One side drives at a time: scroll events arrive a frame or more after
    // the scroll that caused them, so echoing the other side's (older)
    // position back would snap the scroll. The side that scrolls first
    // drives until it has been idle for a moment.
    let driver = null;
    let idleTimer = null;
    const drive = (who, from, to) => {
      if (driver && driver !== who) return;
      driver = who;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        driver = null;
      }, 150);
      if (Math.abs(to.scrollLeft - from.scrollLeft) >= 1) to.scrollLeft = from.scrollLeft;
      // Browsers without scroll-driven animation: mirror the header here.
      if (!SUPPORTS_SCROLL_TIMELINE) head.scrollLeft = body.scrollLeft;
    };
    const onBodyScroll = () => drive("body", body, proxy);
    const onProxyScroll = () => drive("proxy", proxy, body);
    body.addEventListener("scroll", onBodyScroll, { passive: true });
    proxy.addEventListener("scroll", onProxyScroll, { passive: true });
    return () => {
      window.clearTimeout(idleTimer);
      window.removeEventListener("resize", publish);
      if (observer) observer.disconnect();
      body.removeEventListener("scroll", onBodyScroll);
      proxy.removeEventListener("scroll", onProxyScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { headRef, bodyRef, proxyRef, proxyInnerRef };
};

// The sticky header strip. `top` is where it sticks (under the toolbar).
// Hidden on phones, where ApproveTable shows rows as cards.
export const StickyHeadStrip = ({ headRef, top, colWidths, children }) => (
  <Mui.Box sx={{ display: { xs: "none", md: "block" }, position: "sticky", top, zIndex: 3 }}>
    <Mui.Box
      ref={headRef}
      sx={{
        ...fillWithoutWideningSx,
        overflowX: SUPPORTS_SCROLL_TIMELINE ? "hidden" : "auto",
        overflowY: "hidden",
        backgroundColor: designColors.white,
        ...hiddenScrollbarSx,
      }}
    >
      <Mui.Table sx={fixedTableSx(colWidths)}>
        <ColGroup colWidths={colWidths} />
        {children}
      </Mui.Table>
    </Mui.Box>
  </Mui.Box>
);

// Header cells that scroll with the body (every header cell except pinned
// ones). Applied per cell so pinned header cells can stay in place.
export const headScrollCellSx = SUPPORTS_SCROLL_TIMELINE
  ? { animation: `${headShift} auto linear both`, animationTimeline: TIMELINE }
  : {};

// Pinned columns: `pinned` is a list of column indexes (into colWidths).
// Returns { [index]: left offset in px } plus the last pinned index, which
// gets the divider shadow.
export const pinnedColumns = (colWidths, pinned) => {
  const lefts = {};
  let left = 0;
  [...pinned].sort((a, b) => a - b).forEach((index) => {
    lefts[index] = left;
    left += colWidths[index] ?? 0;
  });
  return { lefts, last: pinned.length ? Math.max(...pinned) : -1 };
};

// Only from md up: below that ApproveTable shows rows as cards.
export const pinnedCellSx = ({ left, last, head = false }) => ({
  "@media (min-width:900px)": {
    position: "sticky",
    left,
    zIndex: head ? 4 : 1,
    backgroundColor: head ? designColors.slate100 : designColors.white,
    ...(last ? { boxShadow: `1px 0 0 ${designColors.slate200}, 6px 0 8px -6px rgba(15, 23, 42, 0.18)` } : {}),
  },
});

// The scrolling body box (native bar hidden; the proxy bar replaces it).
export const scrollBodySx = {
  ...fillWithoutWideningSx,
  overflowX: { xs: "visible", md: "auto" },
  overflowY: "visible",
  overscrollBehaviorX: "contain",
  ...hiddenScrollbarSx,
  ...(SUPPORTS_SCROLL_TIMELINE ? { scrollTimeline: `${TIMELINE} x` } : {}),
};

// Sticky proxy scrollbar; `bottom` sits it just above the sticky footer.
export const ProxyScrollbar = ({ proxyRef, proxyInnerRef, bottom = 0 }) => (
  <Mui.Box
    ref={proxyRef}
    sx={{
      position: "sticky",
      bottom,
      zIndex: 4,
      overflowX: "auto",
      overflowY: "hidden",
      ...fillWithoutWideningSx,
      display: "none",
      backgroundColor: designColors.white,
    }}
  >
    <Mui.Box ref={proxyInnerRef} sx={{ height: "1px" }} />
  </Mui.Box>
);

// Zero-height copy of the header for the body table: keeps the column
// widths' header row in the same table (ApproveTable labels phone cards
// from it) without showing it twice.
export const hiddenHeadCellSx = {
  borderBottom: "none",
  py: 0,
  height: 0,
  lineHeight: 0,
  "& > *": { visibility: "hidden", height: 0, overflow: "hidden" },
};
